import { CircularProgress } from '@mui/material';
import { FC, useEffect, useState } from 'react';

import styles from './FramedDashboard.module.scss';
import { dashboardOrigin } from './oth360Text';

const FRAME_INITIAL_HEIGHT = 900;
const FRAME_REVEAL_GRACE_MS = 8000;
const FRAME_MIN_HEIGHT = 400;
const FRAME_MAX_HEIGHT = 8000;

type FramedDashboardProps = {
  /** The dashboard link. A login credential: used here once and never stored. */
  src: string;
  title: string;
  /** Shown with the spinner until the dashboard has rendered. */
  loadingText: string;
  /** The dashboard's own "back" link, when it has one (`360review:back`). */
  onBack?: () => void;
  /**
   * The dashboard's session has run out (`360review:session-expired`, posted
   * by the admin dashboard after its hour). The member dashboard's links do
   * not expire, so the member tab leaves this unset and the message is ignored.
   */
  onSessionExpired?: () => void;
};

/**
 * A 360review dashboard inside a circle tab, exactly as 360review shows it:
 * edge to edge, no strip, no frame around it (Markus, 2026-10-14). The
 * dashboard boots behind a dark "Loading" screen of its own, so the frame is
 * hidden until the dashboard has rendered (its first `360review:height`
 * message), or after a grace period if no message ever comes. The height
 * follows the messages so the page scrolls as one.
 */
export const FramedDashboard: FC<FramedDashboardProps> = ({
  src,
  title,
  loadingText,
  onBack,
  onSessionExpired,
}) => {
  const frameOrigin = dashboardOrigin(src);
  const [frameHeight, setFrameHeight] = useState(FRAME_INITIAL_HEIGHT);
  const [frameReady, setFrameReady] = useState(false);

  // A new link is a new dashboard: start over
  useEffect(() => {
    setFrameHeight(FRAME_INITIAL_HEIGHT);
    setFrameReady(false);
  }, [src]);

  // Messages from the framed dashboard: its height, "back" from its own link,
  // and an expired session
  useEffect(() => {
    if (!frameOrigin) {
      return;
    }
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== frameOrigin) {
        return;
      }
      const data = event.data as { type?: unknown; height?: unknown } | null;
      if (!data || typeof data !== 'object') {
        return;
      }
      if (data.type === '360review:height' && typeof data.height === 'number') {
        setFrameReady(true);
        setFrameHeight(
          Math.min(
            FRAME_MAX_HEIGHT,
            Math.max(FRAME_MIN_HEIGHT, Math.round(data.height))
          )
        );
      } else if (data.type === '360review:back') {
        onBack?.();
      } else if (data.type === '360review:session-expired') {
        onSessionExpired?.();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frameOrigin, onBack, onSessionExpired]);

  useEffect(() => {
    if (frameReady) {
      return undefined;
    }
    const timer = window.setTimeout(
      () => setFrameReady(true),
      FRAME_REVEAL_GRACE_MS
    );
    return () => window.clearTimeout(timer);
  }, [src, frameReady]);

  return (
    <section className={styles.frameContainer}>
      {!frameReady && (
        <div className={styles.loading} role="status" aria-live="polite">
          <CircularProgress
            size={40}
            sx={{ color: 'var(--brand-accent, #f0544f)' }}
          />
          <p className={styles.quiet}>{loadingText}</p>
        </div>
      )}
      <iframe
        className={styles.frame}
        src={src}
        title={title}
        allow="downloads"
        style={
          frameReady
            ? { height: frameHeight }
            : { height: 0, opacity: 0, pointerEvents: 'none' }
        }
      />
    </section>
  );
};
